import * as React from 'react'
import { useState } from 'react'

import {
  Box,
  BoxList,
  Button,
  Text,
  useConfirmationDialog,
} from '@factorialco/deprecated-design-system'
import {
  AtsDeleteJobPostingEvaluationFormDocument,
  AtsJobPostingSettingsEvaluationFormFragment,
  AtsSaveAsTemplateJobPostingEvaluationFormDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EditEvaluationForm } from './EditEvaluationForm'

interface Props {
  item: AtsJobPostingSettingsEvaluationFormFragment
}

export const EvaluationFormsItem = ({ item }: Props) => {
  const [showEditEvaluationForm, setShowEditEvaluationForm] = useState(false)
  const [saveAsTemplate] = useMutation(AtsSaveAsTemplateJobPostingEvaluationFormDocument, {
    extractorFn: (data) => data.atsMutations.saveAsTemplateAtsEvaluationForm,
  })

  const [deleteEvaluationForm] = useMutation(AtsDeleteJobPostingEvaluationFormDocument, {
    extractorFn: (data) => data.atsMutations.deleteAtsEvaluationForm,
  })

  const { openDialog } = useConfirmationDialog()

  const onDelete = async () => {
    const result = await deleteEvaluationForm({
      variables: { id: item.id },
      update(cache) {
        cache.evict({ id: cache.identify(item) })
      },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.removed_successfully'),
      })
    }

    return handled
  }

  const onSaveAsTemplate = async () => {
    const result = await saveAsTemplate({
      variables: { id: item.id },
    })

    const handled = handleResult(result, () => undefined)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    return handled
  }

  return (
    <>
      <BoxList.Item>
        <Box
          width="full"
          alignItems="center"
          justifyContent="spaceBetween"
          flexDirection="row"
          flexGrow
        >
          <Text.Normal weight="medium" color="grey1000" ellipsis>
            {item.name}
          </Text.Normal>

          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative
                type="secondary"
                iconName="ellipsis"
                aria-label={i18n.t(
                  'ats.job_posting_settings.evaluation_forms.actions.more_actions_label'
                )}
              />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="center">
              <PopoverMenu.TouchableOption
                onClick={() => setShowEditEvaluationForm(true)}
                aria-label={i18n.t('ats.job_posting_settings.evaluation_forms.actions.edit_label', {
                  name: item.name,
                })}
                title={i18n.t('common.edit')}
              />
              <PopoverMenu.TouchableOption
                onClick={onSaveAsTemplate}
                aria-label={i18n.t(
                  'ats.job_posting_settings.evaluation_forms.actions.save_as_template_label',
                  { name: item.name }
                )}
                title={i18n.t('ats.job_posting_settings.evaluation_forms.actions.save_as_template')}
              />
              <PopoverMenu.TouchableOption
                onClick={() =>
                  openDialog({
                    title: i18n.t('ats.job_posting_settings.evaluation_forms.delete_modal.title'),
                    illustration: 'alert',
                    content: i18n.t(
                      'ats.job_posting_settings.evaluation_forms.delete_modal.description'
                    ),
                    cancelLabel: i18n.t('common.cancel'),
                    confirmLabel: i18n.t('common.delete'),
                    onConfirm: onDelete,
                  })
                }
                aria-label={i18n.t(
                  'ats.job_posting_settings.evaluation_forms.actions.delete_label',
                  {
                    name: item.name,
                  }
                )}
                title={i18n.t('common.destroy')}
                danger
              />
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      </BoxList.Item>

      {showEditEvaluationForm && (
        <EditEvaluationForm onClose={() => setShowEditEvaluationForm(false)} item={item} />
      )}
    </>
  )
}
